import React, { useEffect, useState, useCallback } from 'react';
import { Page, Header, Content } from '@backstage/core-components';
import {
  Box,
  Typography,
  Link,
  Chip,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import AssignmentIcon from '@material-ui/icons/Assignment';

const useStyles = makeStyles(theme => ({
  content: {
    maxWidth: 1200,
    margin: '0 auto',
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  sectionTitleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionIcon: {
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  listPanel: {
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  listItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  listItem: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.background.default,
    transition: 'background 0.2s',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  listItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  listItemTitle: {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  listItemMeta: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
  },
  statusChip: {
    height: 20,
    fontSize: '0.75rem',
  },
  loadingBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  stats: {
    display: 'flex',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
}));

interface Ticket {
  key: string;
  fields: {
    summary: string;
    status: {
      name: string;
    };
    updated: string;
    priority?: {
      name: string;
    };
  };
  _jiraInstance?: {
    name: string;
    baseUrl: string;
  };
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const AssignedTicketsPage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const fetchTickets = useCallback(
    async (email: string) => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          project: '*',
          assignee: email,
          excludeDoneCategory: 'true',
          maxResults: '100', // Fetch up to 100 tickets
        });

        const response = await fetch(`${backendUrl}/api/jira/search?${params}`);

        if (!response.ok) {
          throw new Error(`Jira request failed: ${response.status}`);
        }

        const data = await response.json();
        setTickets(data.issues || []);
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
    [backendUrl],
  );

  useEffect(() => {
    const init = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email;

        if (email) {
          setUserEmail(email);
          await fetchTickets(email);
        } else {
          setLoading(false);
          setError('Unable to determine user email.');
        }
      } catch (err) {
        console.error('Error initializing:', err);
        setLoading(false);
        setError('Failed to load user information.');
      }
    };

    init();
  }, [identityApi, fetchTickets]);

  return (
    <Page themeId="home">
      <Header
        title="Mis Tickets Asignados"
        subtitle="Todos tus tickets de Jira asignados"
      />
      <Content>
        <Box className={classes.content}>
          {userEmail && (
            <Box className={classes.stats}>
              <Box className={classes.statItem}>
                <Typography className={classes.statValue}>
                  {tickets.length}
                </Typography>
                <Typography className={classes.statLabel}>
                  Tickets Asignados
                </Typography>
              </Box>
              <Box className={classes.statItem}>
                <Typography className={classes.statValue}>
                  {userEmail}
                </Typography>
                <Typography className={classes.statLabel}>
                  User Email
                </Typography>
              </Box>
            </Box>
          )}

          <Box className={classes.section}>
            <Box className={classes.sectionHeader}>
              <Box className={classes.sectionTitleWrap}>
                <AssignmentIcon className={classes.sectionIcon} />
                <Typography className={classes.sectionTitle}>
                  Tickets Abiertos
                </Typography>
              </Box>
            </Box>
            <Box className={classes.listPanel}>
              {loading ? (
                <Box className={classes.loadingBox}>
                  <CircularProgress size={40} />
                  <Typography>Cargando tickets...</Typography>
                </Box>
              ) : error ? (
                <Box className={classes.emptyState}>{error}</Box>
              ) : tickets.length === 0 ? (
                <Box className={classes.emptyState}>
                  No se encontraron tickets asignados.
                </Box>
              ) : (
                <Box className={classes.listItems}>
                  {tickets.map(ticket => {
                    const jiraBaseUrl =
                      ticket._jiraInstance?.baseUrl ||
                      'https://digitalfemsa.atlassian.net';
                    return (
                      <Box key={ticket.key} className={classes.listItem}>
                        <Box className={classes.listItemHeader}>
                          <Link
                            href={`${jiraBaseUrl}/browse/${ticket.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.listItemTitle}
                          >
                            {ticket.key}: {ticket.fields.summary}
                          </Link>
                          <Chip
                            size="small"
                            label={ticket.fields.status.name}
                            className={classes.statusChip}
                          />
                        </Box>
                        <Typography className={classes.listItemMeta}>
                          Actualizado {formatDate(ticket.fields.updated)}
                          {ticket.fields.priority
                            ? ` - ${ticket.fields.priority.name}`
                            : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
